import {httpGet, httpPost, httpPut} from "./http";
import {getActiveAccount, getActivePerson} from "../lib/activeMember";

const accountUrl = window.env.BACKEND_API_URL + '/accounts';
const personUrl = window.env.BACKEND_API_URL + '/people';
const profileUrl = window.env.BACKEND_API_URL + '/profiles';
const registrationUrl = window.env.BACKEND_API_URL + '/registrations';

//region Account

const createAccount = async ({
                                 firstName,
                                 lastName,
                                 birthDate,
                                 email,
                                 password,
                                 personID,
                                businessName,
                                nameOnContract,
                                dbaName,
                                websiteAddress,
                                taxRegistrationNumber,
                                businessRegistrationNumber,
                                industryCode,
                                companySize
                            }, signal) => {
    const body = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        password: password,
    };

    if (personID) body['person_id'] = personID;
    if (birthDate) body['birth_date'] = birthDate;
    if (businessName) body['business_name'] = businessName;
    if (nameOnContract) body['name_on_contract'] = nameOnContract;
    if (dbaName) body['dba_name'] = dbaName;
    if (websiteAddress) body['website_address'] = websiteAddress;
    if (taxRegistrationNumber) body['gst_hst_number'] = taxRegistrationNumber;
    if (businessRegistrationNumber) body['business_registration_number'] = businessRegistrationNumber;
    if (industryCode) body['industry_code'] = industryCode;
    if (companySize) body['company_size'] = companySize;

    return httpPost(registrationUrl, body, signal);
}

const closeAccount = async (accountID, signal) => {
    const closeAccountUrl = accountUrl + '/' + accountID + '/close';

    return await httpPost(closeAccountUrl, '', signal);
};

const getAccountByID = async (accountID, signal) => {
    let url = accountUrl + '/' + accountID;
    return httpGet(url, signal);
};

const getAccount = async (signal) => {
    if (getActiveAccount() === null) {
        throw new Error('Cannot load account ID from storage');
    }
    let accountID = getActiveAccount().id;
    let url = accountUrl + '/' + accountID;
    return httpGet(url, signal);
};

const updateAccount = async (accountID, {...rest}, signal) => {
    const accountUpdateUrl = `${accountUrl}/${accountID}`;
    return await httpPut(accountUpdateUrl, {...rest}, signal);
};

const addExistingPersonToAccount = async ({accountID, personID, profileID}, signal) => {
    const addExistingPersonToAccountURL = `${registrationUrl}/add_existing_person_to_account`

    const body = {
        account_id: accountID,
        person_id: personID,
        profile_id: profileID,
    };

    return await httpPost(addExistingPersonToAccountURL, body, signal);
}

const addNonExistingPersonToAccount = async ({accountID, firstName, lastName, birthDate, email, password}, signal) => {
    const addNonExistingPersonToAccountURL = `${registrationUrl}/add_non_existing_person_to_account`

    const body = {
        account_id: accountID,
        first_name: firstName,
        last_name: lastName,
        birth_date: birthDate,
        email: email,
        password: password,
    };

    return await httpPost(addNonExistingPersonToAccountURL, body, signal);
}

const getRoleColor = (role) => {
    const roleColorMap = {
        booker: 'volcano',
        driver: 'geekblue',
        owner: 'green',
        regular: 'purple',
        shareholder: 'magenta'
    };

    // Extract the role after the dot (e.g. 'user.booker' -> 'booker').
    const roleKey = role.slice(role.indexOf('.') + 1);

    // Return the corresponding color, default to 'red' if not found.
    return roleColorMap[roleKey] || 'red';
};

//endregion
//region Person

const createPerson = async ({firstName, lastName, birthDate}, signal) => {
    const body = {
        first_name: firstName,
        last_name: lastName,
    };

    if (birthDate) body['birth_date'] = birthDate;

    return await httpPost(personUrl, body, signal);
};

const getPerson = async (signal) => {
    let personID = getActivePerson().id;

    const personGetUrl = personUrl + '/' + personID;

    return await httpGet(personGetUrl, signal);
};

const updatePerson = async (personID, {...rest}, signal) => {
    const personUpdateURL = `${personUrl}/${personID}`;

    return await httpPut(personUpdateURL, {...rest}, signal);
};

// Search form request for staff to look up people by name.
// AccountID is a query param to provide data to know if a person is already associated with an account.
const searchPerson = async (name, {page = 1, perPage = 10}, signal) => {

    // The Account Create page is accessible without a staff representing a membership.
    // That means that getActiveAccount() could be null. set to 0 when the route is through the AccountCreate component.
    const accountID = getActiveAccount() ? getActiveAccount().id : 0;
    const personSearchURL = `${personUrl}/search_person?per_page=${perPage}&page=${page}&name=${name}&account_id=${accountID}`;

    return await httpGet(personSearchURL, signal)
}

//endregion
//region Profile

const getProfile = async (profileID, signal) => {
    const getProfileUrl = profileUrl + '/' + profileID;
    return await httpGet(getProfileUrl, signal);
};

// Fetch the profile resources for the AccountDriverManagementTable component. Has pagination.
// roleManagement is a param that defines which resource is returned from profile index route.
// activeMemberships is a param that defines if we are retrieving currently active memberships, or inactive memberships.
const getAccountProfilesDriverManagement = async ({page = 1, perPage = 10, accountID, activeMemberships}, signal) => {
    const roleManagement = 0;
    const queryString = `?per_page=${perPage}&page=${page}&account_id=${accountID}&role_management=${roleManagement}&active_memberships=${activeMemberships}`;

    const profileIndexUrl = queryString ? `${profileUrl}/${queryString}` : profileUrl;

    return await httpGet(profileIndexUrl, signal);
};

// Fetch the profile resources for the AccountRoleManagement component. Does not have pagination.
// role_management is a param that defines which resource is returned from profile index route.
// activeMemberships is a param that defines if we are retrieving currently active memberships, or inactive memberships.
const getAccountProfilesRoleManagement = async (accountID, signal) => {
    const roleManagement = 1;
    const activeMemberships = 1;
    const queryString = `?account_id=${accountID}&role_management=${roleManagement}&active_memberships=${activeMemberships}`;

    const profileIndexUrl = queryString ? `${profileUrl}/${queryString}` : profileUrl;

    return await httpGet(profileIndexUrl, signal);
};

const createProfile = async ({personID, userID, category, company}, signal) => {
    const body = {};

    if (personID) body['person_id'] = personID;
    if (userID) body['user_id'] = userID;
    if (category) body['category'] = category;
    if (company) body['company'] = company;

    return await httpPost(profileUrl, body, signal);
};

const updateProfile = async (profileID, {personID, userID, category, company}, signal) => {
    const profileUpdateUrl = `${profileUrl}/${profileID}`;

    const body = {};

    if (personID) body['person_id'] = personID;
    if (userID) body['user_id'] = userID;
    if (category) body['category'] = category;
    if (company) body['company'] = company;

    return await httpPut(profileUpdateUrl, body, signal);
};

// Note: syncRolesToProfile has been renamed to updateProfileRoles in accountApi.ts.
// When refactoring AccountCreate, be sure to use the new name to avoid duplication.
const syncRolesToProfile = async (profileID, accountID = null, roles, signal) => {
    const profileRolesUrl = `${profileUrl}/${profileID}/sync_roles`;

    const body = {};
    body['account_id'] = accountID;
    body['roles'] = roles;

    return await httpPost(profileRolesUrl, body, signal);
};

// Returns all available roles in the system, with type and subtype optional parameters.
const getAllAvailableRoles = async (roleType, roleSubType, signal) => {
    const profileAvailableRolesUrl = `${profileUrl}/available_roles?role_type=${roleType}&role_sub_type=${roleSubType}`;

    return await httpGet(profileAvailableRolesUrl, signal);
};

//endregion

export {
    createAccount,
    closeAccount,
    getAccount,
    getAccountByID,
    addExistingPersonToAccount,
    addNonExistingPersonToAccount,
    updateAccount,
    getRoleColor,
    createPerson,
    getPerson,
    searchPerson,
    updatePerson,
    getProfile,
    getAccountProfilesDriverManagement,
    getAccountProfilesRoleManagement,
    createProfile,
    updateProfile,
    getAllAvailableRoles,
    syncRolesToProfile,
};
